// services/techSkillVendor.service.ts
import {
  AsyncSelectFetchParams,
  AsyncSelectFetchResult,
} from "@/components/form/types";

export type TechSkillVendorStatus = "active" | "inactive" | "pending";

export interface TechSkillVendorType {
  _id: string;
  vendorName: string; // e.g. "CodeMaster Institute"
  contactPerson: string; // e.g. "David Kim"
  email: string; // e.g. "david@codemaster.com"
  phone: string;
  website?: string;
  industry: string[];
  city: string;
  state: string;
  preferredDepartments: string[];
  rating: number;
  completedPrograms: number; // Renamed from programsOffered
  technologies: string[]; // e.g. ["Python", "Java", "React", "Node.js"]
  description: string;
  status: TechSkillVendorStatus;
  createdAt: string;
  updatedAt: string;
}

export interface TechSkillVendorCreateType {
  vendorName: string;
  contactPerson: string;
  email: string;
  phone: string;
  website?: string;
  industry: string[];
  city: string;
  state: string;
  preferredDepartments: string[];
  rating: number;
  completedPrograms: number;
  technologies: string[];
  description: string;
  status: TechSkillVendorStatus;
}

export interface TechSkillVendorMetaType {
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export type TechSkillVendorParamsType = {
  search?: string;
  status?: TechSkillVendorStatus | "all";
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  page?: number;
  limit?: number;
};

// ─── Dummy Data ───────────────────────────────────────────────────────────────

const DUMMY_TECH_SKILL_VENDORS: TechSkillVendorType[] = [
  {
    _id: "1",
    vendorName: "CodeMaster Institute",
    contactPerson: "David Kim",
    email: "david@codemaster.com",
    phone: "+1-555-0201",
    website: "https://codemaster.com",
    industry: ["IT Services", "EdTech"],
    city: "San Francisco",
    state: "CA",
    preferredDepartments: ["CSE", "ISE"],
    rating: 4.8,
    completedPrograms: 12,
    technologies: ["Python", "Java", "React", "Node.js"],
    description: "Leading coding bootcamp and training institute.",
    status: "active",
    createdAt: "2024-01-01T00:00:00.000Z",
    updatedAt: "2024-01-01T00:00:00.000Z",
  },
  {
    _id: "2",
    vendorName: "DataScience Hub",
    contactPerson: "Amanda Lee",
    email: "amanda@datasciencehub.com",
    phone: "+1-555-0202",
    industry: ["Analytics", "Consulting"],
    city: "New York",
    state: "NY",
    preferredDepartments: ["CSE", "ISE", "AIML"],
    rating: 4.6,
    completedPrograms: 8,
    technologies: ["ML", "AI", "Data Analytics"],
    description: "Specialized in Data Science and AI training.",
    status: "active",
    createdAt: "2024-01-02T00:00:00.000Z",
    updatedAt: "2024-01-02T00:00:00.000Z",
  },
  {
    _id: "3",
    vendorName: "CloudTech Academy",
    contactPerson: "Steve Martinez",
    email: "steve@cloudtech.com",
    phone: "+1-555-0203",
    industry: ["Cloud Computing"],
    city: "Austin",
    state: "TX",
    preferredDepartments: ["CSE", "ISE"],
    rating: 4.7,
    completedPrograms: 10,
    technologies: ["AWS", "Azure", "Docker", "Kubernetes"],
    description: "Expert cloud computing training services.",
    status: "active",
    createdAt: "2024-01-03T00:00:00.000Z",
    updatedAt: "2024-01-03T00:00:00.000Z",
  },
  {
    _id: "4",
    vendorName: "WebDev Pro",
    contactPerson: "Sarah Connor",
    email: "sarah@webdevpro.com",
    phone: "+1-555-0204",
    industry: ["Web Development"],
    city: "Seattle",
    state: "WA",
    preferredDepartments: ["CSE"],
    rating: 4.5,
    completedPrograms: 6,
    technologies: ["HTML", "CSS", "JavaScript", "TypeScript"],
    description: "Comprehensive web development courses.",
    status: "pending",
    createdAt: "2024-01-04T00:00:00.000Z",
    updatedAt: "2024-01-04T00:00:00.000Z",
  },
];

const MOCK_TECHNOLOGIES = [
  "Python",
  "Java",
  "React",
  "Node.js",
  "Angular",
  "Vue.js",
  "AWS",
  "Azure",
  "Docker",
  "Kubernetes",
  "Machine Learning",
  "Data Science",
  "Cybersecurity",
  "Blockchain",
  "DevOps",
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const simulateDelay = (ms = 400) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const applyFilters = (
  vendors: TechSkillVendorType[],
  filters: TechSkillVendorParamsType
): { data: TechSkillVendorType[]; total: number } => {
  let result = [...vendors];
  if (filters.search) {
    const q = filters.search.toLowerCase();
    result = result.filter(
      (v) =>
        v.vendorName.toLowerCase().includes(q) ||
        v.contactPerson.toLowerCase().includes(q) ||
        v.email.toLowerCase().includes(q) ||
        v.technologies.some((t) => t.toLowerCase().includes(q))
    );
  }
  if (filters.status && filters.status !== "all") {
    result = result.filter((v) => v.status === filters.status);
  }
  const total = result.length;
  const page = filters.page ?? 1;
  const limit = filters.limit ?? 10;
  result = result.slice((page - 1) * limit, page * limit);
  return { data: result, total };
};

// ─── Service Functions ────────────────────────────────────────────────────────

export const fetchAllTechSkillVendors = async (
  filters: TechSkillVendorParamsType
) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.get("/vendors/tech-skill/", { params: filters });
  const limit = filters.limit ?? 10;
  const page = filters.page ?? 1;
  const { data, total } = applyFilters(DUMMY_TECH_SKILL_VENDORS, filters);
  return {
    vendors: data,
    metaData: {
      pagination: { total, page, limit, totalPages: Math.ceil(total / limit) },
    } as TechSkillVendorMetaType,
  };
};

export const fetchTechSkillVendorById = async (_id: string) => {
  await simulateDelay();
  const vendor = DUMMY_TECH_SKILL_VENDORS.find((v) => v._id === _id);
  if (!vendor) throw new Error("Tech skill vendor not found");
  return vendor;
};

export const createTechSkillVendor = async (
  data: TechSkillVendorCreateType
) => {
  await simulateDelay();
  const newVendor: TechSkillVendorType = {
    ...data,
    _id: String(Date.now()),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  DUMMY_TECH_SKILL_VENDORS.push(newVendor);
  return newVendor;
};

export const updateTechSkillVendor = async (
  _id: string,
  data: TechSkillVendorCreateType
) => {
  await simulateDelay();
  const index = DUMMY_TECH_SKILL_VENDORS.findIndex((v) => v._id === _id);
  if (index === -1) throw new Error("Tech skill vendor not found");
  DUMMY_TECH_SKILL_VENDORS[index] = {
    ...DUMMY_TECH_SKILL_VENDORS[index],
    ...data,
    updatedAt: new Date().toISOString(),
  };
  return DUMMY_TECH_SKILL_VENDORS[index];
};

export const deleteTechSkillVendor = async (_id: string) => {
  await simulateDelay();
  const index = DUMMY_TECH_SKILL_VENDORS.findIndex((v) => v._id === _id);
  if (index === -1) throw new Error("Tech skill vendor not found");
  const [deleted] = DUMMY_TECH_SKILL_VENDORS.splice(index, 1);
  return deleted;
};

export const fetchTechnologiesOptions = async (
  params: AsyncSelectFetchParams
): Promise<AsyncSelectFetchResult> => {
  await simulateDelay(300);
  const search = params?.search?.toLowerCase() || "";
  const filtered = MOCK_TECHNOLOGIES.filter((t) =>
    t.toLowerCase().includes(search)
  );
  return {
    items: filtered.map((t) => ({ label: t, value: t })),
    hasMore: false,
    total: filtered.length,
  };
};

export const techSkillVendorService = {
  fetchAllTechSkillVendors,
  fetchTechSkillVendorById,
  createTechSkillVendor,
  updateTechSkillVendor,
  deleteTechSkillVendor,
  fetchTechnologiesOptions,
};
